import { track } from "@/lib/analytics/track";
import { KNOWN_EVENT_NAMES, KNOWN_FEATURES, type AnalyticsFeature, type EventName } from "@/lib/analytics/events";

function asKnownEventName(value: string | null | undefined): EventName | null {
  if (!value) return null;
  const v = value.trim() as EventName;
  return (KNOWN_EVENT_NAMES as readonly string[]).includes(v) ? v : null;
}

function parseNumber(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function asFeature(value: string | null | undefined): AnalyticsFeature | undefined {
  if (!value) return undefined;
  const v = value.trim() as AnalyticsFeature;
  return (KNOWN_FEATURES as readonly string[]).includes(v) ? v : undefined;
}

export function installAutoClickTracking(): () => void {
  if (typeof document === "undefined") return () => {};

  const handler = (event: MouseEvent) => {
    const target = event.target as Element | null;
    if (!target) return;

    const el = target.closest<HTMLElement>("[data-analytics-event]");
    if (!el) return;

    const requestedEvent = el.dataset.analyticsEvent;
    const knownName = asKnownEventName(requestedEvent);

    // Standardized UI click event (scales well without exploding event names).
    // If someone specifies a known typed event name, we'll fire that instead.
    if (knownName && knownName !== "ui_click") {
      // We can't safely infer params for arbitrary typed events here, so keep auto-click to ui_click
      // and preserve the requested name in params.
      track("ui_click", {
        element_id: requestedEvent,
        label: el.dataset.analyticsLabel,
        feature: asFeature(el.dataset.analyticsFeature),
        location: el.dataset.analyticsLocation,
        value: parseNumber(el.dataset.analyticsValue),
        requested_event_name: requestedEvent,
      });
      return;
    }

    track("ui_click", {
      element_id: el.dataset.analyticsElementId ?? requestedEvent ?? undefined,
      label: el.dataset.analyticsLabel,
      feature: asFeature(el.dataset.analyticsFeature),
      location: el.dataset.analyticsLocation,
      value: parseNumber(el.dataset.analyticsValue),
      requested_event_name: requestedEvent && requestedEvent !== "ui_click" ? requestedEvent : undefined,
    });
  };

  // Capture phase so we still get events even if propagation is stopped.
  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
}


